import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, ClipboardList, CloudOff, Download, Loader2, Mail, QrCode, Search, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { isNetworkError } from "../../lib/offlineQueue";
import { useOfflineSync } from "../../hooks/useOfflineSync";
import { SyncBanner } from "./SyncBanner";
import { Input } from "../ui/input";
import { AdminGate } from "./AdminGate";
import { CardScanSheet } from "./CardScanSheet";
import { KioskMode } from "./KioskMode";
import { EventQrPanel } from "./EventQrPanel";
import { ContactEditSheet } from "./ContactEditSheet";
import { CardThumb } from "./CardThumb";
import { FollowUpSheet } from "./FollowUpSheet";
import { OutlookContactsButton } from "./OutlookContactsButton";

const SOURCE = { card: "Card", kiosk: "Sign-up", qr: "QR", manual: "Manual" };

const ActionTile = ({ icon: Icon, label, hint, onClick, testid, dark }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex flex-col items-start gap-2 rounded-3xl p-4 text-left active:scale-[0.98] transition-transform ${dark ? "bg-brand-charcoal text-white" : "bg-white border border-black/10 text-brand-charcoal"}`}
  >
    <Icon className={`h-6 w-6 ${dark ? "text-brand-gold" : "text-brand-teal"}`} />
    <span className="font-display font-black text-[17px] leading-tight">{label}</span>
    <span className={`text-[11.5px] leading-snug ${dark ? "text-white/60" : "text-brand-charcoal/55"}`}>{hint}</span>
  </button>
);

const PendingRow = ({ p }) => (
  <div data-testid={`event-pending-${p.qid}`} className="w-full flex items-center gap-3 rounded-2xl bg-white/70 border border-dashed border-brand-charcoal/25 p-3">
    <div className="h-12 w-12 rounded-full bg-brand-gold/20 text-brand-charcoal flex items-center justify-center shrink-0">
      {p.type === "card" ? <Camera className="h-5 w-5" /> : <CloudOff className="h-5 w-5" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-bold text-[14px] text-brand-charcoal truncate">
        {p.type === "card" ? "Business card photo" : p.payload?.name || p.payload?.email || p.payload?.phone}
      </p>
      <p className="text-[12px] text-brand-charcoal/60 truncate">
        {p.error ? "Couldn't upload — will retry" : p.type === "card" ? "Will be read when back online" : "Waiting to upload"}
      </p>
    </div>
    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-brand-gold-dark shrink-0">On phone</span>
  </div>
);

const ContactRow = ({ c, onOpen }) => (
  <button type="button" onClick={() => onOpen(c)} data-testid={`event-contact-${c.id}`} className="w-full flex items-center gap-3 rounded-2xl bg-white border border-black/10 p-3 text-left">
    {c.card_image_id ? (
      <CardThumb fileId={c.card_image_id} className="h-12 w-16 rounded-lg shrink-0" />
    ) : (
      <div className="h-12 w-12 rounded-full bg-brand-teal/10 text-brand-teal font-black flex items-center justify-center shrink-0">
        {(c.name || c.email || "?").slice(0, 1).toUpperCase()}
      </div>
    )}
    <div className="min-w-0 flex-1">
      <p className="font-bold text-[14px] text-brand-charcoal truncate">{c.name || c.email || c.phone || "Unread card"}</p>
      <p className="text-[12px] text-brand-charcoal/60 truncate">{[c.job_title, c.company].filter(Boolean).join(" · ") || c.email || c.phone}</p>
    </div>
    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/45 shrink-0 text-right">
      {SOURCE[c.source] || c.source}
      {c.needs_review && <span className="block text-brand-gold-dark" data-testid={`event-contact-review-${c.id}`}>Check details</span>}
      {c.outlook_contact_id && <span className="block text-[#0a64ad]" data-testid={`event-contact-outlook-${c.id}`}>In Outlook</span>}
      {c.followup_sent_at && <span className="block text-brand-teal" data-testid={`event-contact-emailed-${c.id}`}>Emailed</span>}
      {c.unsubscribed_at && <span className="block text-red-500" data-testid={`event-contact-unsubscribed-${c.id}`}>Unsubscribed</span>}
    </span>
  </button>
);

const downloadCsv = async (event) => {
  try {
    const res = await api.get(`/admin/events/${event.id}/contacts.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${event.name.replace(/[^A-Za-z0-9_-]+/g, "-")}-contacts.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error("Couldn't export contacts.");
  }
};

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [mode, setMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${id}`);
      setEvent(data.event);
      setContacts(data.contacts);
      try {
        localStorage.setItem(`fff_event_${id}`, JSON.stringify({ event: data.event, contacts: data.contacts.slice(0, 300) }));
      } catch { /* storage full — offline copy is best-effort */ }
    } catch (err) {
      const cached = isNetworkError(err) && localStorage.getItem(`fff_event_${id}`);
      if (cached) {
        const c = JSON.parse(cached);
        setEvent(c.event);
        setContacts(c.contacts);
        return;
      }
      toast.error(isNetworkError(err) ? "No signal, and this event wasn't opened on this phone before." : "Event not found");
      navigate("/admin/events");
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  const { online, pending, syncing, syncNow } = useOfflineSync(id, load);

  const added = (c) => setContacts((prev) => [c, ...prev]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return contacts;
    return contacts.filter((c) => ["name", "email", "phone", "company", "job_title"].some((k) => (c[k] || "").toLowerCase().includes(s)));
  }, [contacts, q]);

  const toggleSignup = async () => {
    try {
      const { data } = await api.patch(`/admin/events/${id}`, { signup_open: !event.signup_open });
      setEvent((e) => ({ ...e, ...data.event }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't update.");
    }
  };
  const deleteEvent = async () => {
    if (!window.confirm(`Delete "${event.name}" and all ${contacts.length} contacts? This can't be undone.`)) return;
    await api.delete(`/admin/events/${id}`);
    navigate("/admin/events");
  };

  if (!event) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-teal" /></div>;

  return (
    <div className="min-h-screen bg-brand-cream/50" data-testid="event-detail-page">
      <div className="max-w-lg mx-auto px-4 pt-5 pb-16">
        <Link to="/admin/events" data-testid="event-detail-back" className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60">
          <ArrowLeft className="h-3.5 w-3.5" /> Events
        </Link>
        <h1 data-testid="event-detail-name" className="mt-3 font-display font-black text-3xl text-brand-charcoal leading-tight">{event.name}</h1>
        <p className="mt-1 text-[13px] text-brand-charcoal/60">
          {[event.event_date, event.location].filter(Boolean).map((t) => `${t} · `).join("")}<b data-testid="event-detail-count">{contacts.length}</b> contact{contacts.length === 1 ? "" : "s"}
          {pending.length > 0 && <span data-testid="event-detail-pending-count"> · +{pending.length} on this phone</span>}
        </p>

        <SyncBanner online={online} pending={pending} syncing={syncing} onSync={syncNow} />

        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <ActionTile dark icon={Camera} label="Scan card" hint="Snap a business card" onClick={() => setMode("scan")} testid="event-action-scan" />
          <ActionTile icon={ClipboardList} label="Sign-up sheet" hint="Hand guests your phone" onClick={() => setMode("kiosk")} testid="event-action-kiosk" />
          <ActionTile icon={QrCode} label="QR code" hint="Guests sign up on their phone" onClick={() => setMode("qr")} testid="event-action-qr" />
          <ActionTile icon={UserPlus} label="Add by hand" hint="Type in a contact" onClick={() => setEditing({})} testid="event-action-manual" />
        </div>
        <button
          type="button"
          onClick={() => setMode("followup")}
          disabled={!contacts.some((c) => c.email && !c.unsubscribed_at)}
          data-testid="event-action-followup"
          className="mt-2.5 w-full flex items-center gap-3 rounded-3xl bg-brand-teal text-white p-4 text-left disabled:opacity-50 active:scale-[0.99] transition-transform"
        >
          <Mail className="h-6 w-6 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block font-display font-black text-[17px]">Send thank-you email</span>
            <span className="block text-[11.5px] text-white/75">Preview it, pick who gets it, then approve.</span>
          </span>
          <span className="text-[11px] font-bold shrink-0" data-testid="event-followup-sent-count">
            {contacts.filter((c) => c.followup_sent_at).length} sent
          </span>
        </button>

        <label className="mt-4 flex items-center justify-between rounded-2xl bg-white border border-black/10 px-4 py-3">
          <span className="text-[13px] text-brand-charcoal"><b>QR sign-ups open</b><span className="block text-[11.5px] text-brand-charcoal/55">Turn off after the event to stop new sign-ups.</span></span>
          <input type="checkbox" checked={!!event.signup_open} onChange={toggleSignup} data-testid="event-signup-open-toggle" className="h-5 w-5 accent-brand-teal" />
        </label>

        <div className="mt-6 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts" data-testid="event-contacts-search" className="pl-9 h-11 rounded-full bg-white border-black/10 text-[15px]" />
          </div>
          <button type="button" onClick={() => downloadCsv(event)} data-testid="event-export-csv" className="h-11 px-4 rounded-full bg-white border border-black/10 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.08em]">
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>
        <OutlookContactsButton event={event} contacts={contacts} onDone={load} />
        <div className="mt-3 space-y-2" data-testid="event-contacts-list">
          {pending.map((p) => <PendingRow key={p.qid} p={p} />)}
          {filtered.length === 0 && pending.length === 0 && <p className="text-center text-[13px] text-brand-charcoal/55 py-8" data-testid="event-contacts-empty">No contacts yet.</p>}
          {filtered.map((c) => <ContactRow key={c.id} c={c} onOpen={setEditing} />)}
        </div>

        <button type="button" onClick={deleteEvent} data-testid="event-delete" className="mt-10 mx-auto flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-[0.1em] text-red-600/80">
          <Trash2 className="h-3.5 w-3.5" /> Delete event
        </button>
      </div>

      {mode === "scan" && <CardScanSheet eventId={event.id} onSaved={added} onClose={() => setMode(null)} />}
      {mode === "kiosk" && <KioskMode event={event} onSaved={added} onExit={() => setMode(null)} />}
      {mode === "qr" && <EventQrPanel event={event} onClose={() => setMode(null)} />}
      {mode === "followup" && (
        <FollowUpSheet
          event={event}
          contacts={contacts}
          onClose={() => setMode(null)}
          onSent={() => { setMode(null); setTimeout(load, 2500); }}
        />
      )}
      {editing && (
        <ContactEditSheet
          eventId={event.id}
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={(c, isNew) => {
            setContacts((prev) => (isNew ? [c, ...prev] : prev.map((x) => (x.id === c.id ? c : x))));
            setEditing(null);
          }}
          onDeleted={(cid) => { setContacts((prev) => prev.filter((x) => x.id !== cid)); setEditing(null); }}
        />
      )}
    </div>
  );
}

export default function EventDetailPage() {
  return <AdminGate><EventDetail /></AdminGate>;
}
